import { Check } from 'lucide-react';

const WoodenPallets = () => {
  const palletTypes = [
    {
      title: 'Pine Wood Pallets',
      description: 'Lightweight and economical pallets made from high-quality pine wood, ideal for light to medium loads.',
      specs: ['Load capacity: 500-1500 kg', 'Sizes: 1000x1200mm, 1200x1200mm, custom', 'Two-way and four-way entry options', 'ISPM-15 heat treated'],
    },
    {
      title: 'Hardwood Pallets',
      description: 'Robust and durable pallets constructed from premium hardwood for heavy-duty applications.',
      specs: ['Load capacity: 1500-3000 kg', 'Ideal for heavy machinery and equipment', 'Long service life', 'Moisture resistant'],
    },
    {
      title: 'Jungle Wood Pallets',
      description: 'Eco-friendly pallets made from sustainably sourced jungle wood, offering excellent strength.',
      specs: ['Load capacity: 1000-2000 kg', 'Natural durability', 'Cost-effective solution', 'Export compliant'],
    },
    {
      title: 'Two-Way & Four-Way Pallets',
      description: 'Versatile pallet designs allowing forklift entry from multiple sides for efficient handling.',
      specs: ['Two-way: Entry from 2 opposite sides', 'Four-way: Entry from all 4 sides', 'Customizable dimensions', 'Standard and custom load capacities'],
    },
    {
      title: 'Heavy-Duty Pallets',
      description: 'Reinforced pallets engineered for extreme loads and harsh working conditions.',
      specs: ['Load capacity: 3000+ kg', 'Extra reinforcement', 'Industrial-grade construction', 'Suitable for machinery and metal goods'],
    },
    {
      title: 'Custom Size Pallets',
      description: 'Pallets manufactured to your exact specifications, optimized for your storage and transport needs.',
      specs: ['Any dimension available', 'Custom load capacity design', 'Optimized for your warehouse', 'Fast turnaround time'],
    },
  ];

  return (
    <div className="font-inter" data-testid="wooden-pallets-page">
      {/* Header */}
      <section
        className="relative bg-cover bg-center h-96 flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.pexels.com/photos/9498584/pexels-photo-9498584.jpeg)'
        }}
        data-testid="wooden-pallets-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">
            Wooden Pallets
          </h1>
          <p className="text-xl text-gray-200">
            ISPM-15 certified pallets in various wood types and specifications
          </p>
          <div className="w-24 h-1 bg-[#B11217] mt-4"></div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white" data-testid="wooden-pallets-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-gray-600 leading-relaxed text-lg mb-4">
              Our wooden pallets are manufactured to the highest quality standards, ensuring safe storage and transportation of your goods. Each pallet is heat treated and ISPM-15 certified, making them suitable for international export.
            </p>
            <p className="text-gray-600 leading-relaxed text-lg">
              We offer a wide range of pallet types and sizes to accommodate various industries and load requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Pallet Types */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="pallet-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-12 text-center">
            Types of Wooden Pallets
          </h2>
          <div className="space-y-8">
            {palletTypes.map((pallet, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                data-testid={`pallet-type-${index}`}
              >
                <h3 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-3">
                  {pallet.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {pallet.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pallet.specs.map((spec, specIndex) => (
                    <div key={specIndex} className="flex items-start gap-2">
                      <Check size={20} className="text-[#B11217] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#B11217]" data-testid="wooden-pallets-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-poppins font-bold text-3xl mb-4">
            Need Custom Wooden Pallets?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Contact us for tailored solutions that match your exact specifications.
          </p>
          <a
            href="/contact"
            className="bg-white text-[#B11217] px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors inline-block"
            data-testid="wooden-pallets-cta-button"
          >
            Request a Quote
          </a>
        </div>
      </section>
    </div>
  );
};

export default WoodenPallets;